/**
 * Режим запуска и платформа (CLIENT_IOS_WEBAPP.md §1.2).
 *
 * §1.2 п. 1–3: режим определяется один раз при старте и держится в памяти;
 * между запусками НЕ кэшируется (п. 4) — один и тот же origin запускается
 * и как вкладка Safari, и как установленное приложение.
 *
 * Признак standalone — дизъюнкция `display-mode: standalone` и
 * `navigator.standalone`; user-agent как единственный признак ЗАПРЕЩЁН.
 */

export type LaunchMode =
  /** Установленное на экран «Домой» приложение на iPhone/iPad — весь CLIENT_IOS_WEBAPP.md. */
  | 'installed_web_app'
  /** Вкладка Safari на iOS: баннер PWA-01, push недоступен. */
  | 'safari_tab'
  /** Обычный web — CLIENT_WEB.md. */
  | 'web'

/** §9.4 п. 4 CLIENT_WEB.md: standalone меняет оболочку, но не набор функций. */
export function isStandalone(): boolean {
  if (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) return true
  // iOS Safari до поддержки display-mode сообщает об этом собственным флагом.
  return (navigator as Navigator & { standalone?: boolean }).standalone === true
}

/**
 * §1.2 п. 2: iOS — по `navigator.platform` либо «Mac с сенсорным экраном»
 * (iPadOS представляется настольным Safari). User-agent — запасной признак
 * для сборок, где `platform` пуст.
 */
export function isIosLike(): boolean {
  if (typeof navigator === 'undefined') return false

  const platform = navigator.platform ?? ''
  const ua = navigator.userAgent ?? ''

  if (/iP(hone|ad|od)/.test(platform) || /iP(hone|ad|od)/.test(ua)) return true
  return navigator.maxTouchPoints > 1 && /Mac/.test(platform)
}

let cachedMode: LaunchMode | null = null

/** Режим запуска, вычисленный один раз за жизнь вкладки (§1.2 п. 1). */
export function launchMode(): LaunchMode {
  if (cachedMode) return cachedMode

  const ios = isIosLike()
  if (ios && isStandalone()) cachedMode = 'installed_web_app'
  else if (ios) cachedMode = 'safari_tab'
  else cachedMode = 'web'

  return cachedMode
}

/** Только для тестов: сброс памяти режима между сценариями. */
export function resetLaunchModeForTests(): void {
  cachedMode = null
}

/**
 * Жест «назад» от края принадлежит браузеру: Safari на iPhone/iPad во вкладке сам
 * анимирует возврат по истории (и вперёд) снимками страниц. Свой жест рамки экрана
 * там не нужен, а анимация входа после такого перехода была бы второй подряд.
 * В установленном Web App системного жеста нет — там работает свой (§4.4).
 */
export function browserOwnsBackGesture(): boolean {
  return isIosLike() && !isStandalone()
}
